<template>
  <div ref="tableList" class="data-table-list">
    <!-- Named slot for the selection-bar-->
    <slot name="selection-bar" />

    <!-- Default slot for the data-table -->
    <slot />
  </div>
</template>

<script setup lang="ts">
import DOMUtils from "@utils/dom.utils";
import { isDefined } from "@utils/var.check";
import { ref, computed, onMounted, onUnmounted, nextTick } from "vue";

// Props
const props = defineProps({
  scrollContainerSelector: { type: String, default: undefined },
  inSubpage: { type: Boolean, default: false },
});

const tableList = ref<HTMLElement | null>(null);
const scrollContainer = ref<HTMLElement | Window>(window);
const showBackToTop = ref(false);

// Computed
const contentSubpageScrollContainerSelector = computed(
  () => ".content-subpage.is-visible .bx--modal-container>.content>.main",
);

// Methods
const updateShowBackToTop = () => {
  if (tableList.value) {
    showBackToTop.value = tableList.value.getBoundingClientRect().top < 85;
  }
};

const handleScroll = () => {
  updateShowBackToTop();
};

// Mounted
onMounted(() => {
  nextTick(() => {
    if (props.inSubpage) {
      const el = DOMUtils.el(contentSubpageScrollContainerSelector.value);
      if (el) scrollContainer.value = el;
    }
    if (isDefined(props.scrollContainerSelector)) {
      const el = DOMUtils.el(props.scrollContainerSelector);
      if (el) scrollContainer.value = el;
    }

    if (scrollContainer.value && typeof scrollContainer.value.addEventListener === "function") {
      scrollContainer.value.addEventListener("scroll", handleScroll);
      updateShowBackToTop();
    }
  });
});

onUnmounted(() => {
  if (scrollContainer.value && typeof scrollContainer.value.removeEventListener === "function") {
    scrollContainer.value.removeEventListener("scroll", handleScroll);
  }
});
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.data-table-list {
  > .selection-bar {
    &._active {
      position: relative; // to ensure zindex is applied in datatable
      border-radius: 10px 10px 0 0;
      + .v-table {
        border-radius: 0 0 10px 10px;
        // The wrapper doesn't include the pagination, so it should not have any border radius
        > .v-table__wrapper {
          border-radius: 0;

          > table {
            > thead {
              > tr {
                > th {
                  &:first-child {
                    // Higher specificity to override styling from Vuetify
                    border-radius: 0;
                  }
                  &:last-child {
                    // Higher specificity to override styling from Vuetify
                    border-radius: 0;
                  }
                }
              }
            }
          }
        }
      }
    }
  }

  > .v-table {
    border-radius: 10px;
    > .v-table__wrapper {
      border-radius: 10px 10px 0 0;
    }
  }

  &.result-list-table-view-datatable {
    flex: 0 0 100%;
    max-width: 100%;
    padding-bottom: $floating-action-buttons-spacing;
    margin-top: $spacing-06;

    .v-data-table__wrapper {
      .v-data-table__progress {
        display: none;
      }

      > table {
        > thead {
          > tr {
            > th {
              border-right: 1px solid $ui-04;
            }
          }
        }
      }
    }
  }
}
</style>
